import { useMemo } from 'react';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import { Reference } from 'types/reference';

export interface HeroProps {
  image: ImageProps;
  title: string;
  subtitle: string;
  ctaLabel?: string;
  ctaReference?: Reference;
  featuredLabel?: string;
  featuredReference?: Reference;
}

const Hero: React.FC<HeroProps> = ({
  image,
  title,
  subtitle,
  ctaLabel,
  ctaReference,
  featuredLabel,
  featuredReference,
}) => {
  const { formatMessage } = useFormat({ name: 'common' });
  const cropFocus = useMemo(() => {
    if (image.gravity?.coordinates) {
      const right = Math.ceil((image.gravity?.coordinates.x / (image.media?.width as number)) * 100);
      const top = Math.ceil((image.gravity?.coordinates.y / (image.media?.height as number)) * 100);

      return `right ${right.toString()}% top ${top.toString()}%`;
    }

    if (image.gravity?.mode === 'center') {
      return 'center';
    }

    return 'initial';
  }, [image]);

  return (
    <div className="relative w-full">
      <div className="relative h-[35.375rem] md:h-[35.375rem]">
        <Image
          {...image}
          priority
          loading="eager"
          alt={title}
          fill
          style={{ objectFit: 'cover', objectPosition: cropFocus }}
          className="brightness-75"
        />

        <div className="relative h-full">
          <div className="container flex h-full flex-col justify-end py-64 md:flex-row md:items-end md:justify-between md:py-32 lg:py-64">
            <div className="flex flex-col gap-32">
              <div className="text-inverted flex flex-col gap-16 font-medium">
                <Typography as="h1" className="headline-1 tracking-[0.4px]">
                  {title}
                </Typography>
                <Typography className="body-text">{subtitle}</Typography>
              </div>

              {ctaLabel && (
                <Link link={ctaReference} buttonVariant="primary" className="inline-block w-fit">
                  {ctaLabel}
                </Link>
              )}
            </div>

            {featuredLabel && (
              <div className="mt-32 flex md:justify-end">
                <div className="text-inverted flex items-center">
                  <Typography className="body-text-small font-medium">
                    {`${formatMessage({ id: 'featured', defaultMessage: 'Featured' })}:`}
                  </Typography>
                  <div className="px-8 py-2">
                    <Link link={featuredReference} variant="primary" className="!text-inverted font-bold underline">
                      {featuredLabel}
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
